// The campaign builder's header: the name, the status chip, the actions, and the
// action banner under them.

import { CampaignDeliveryAction } from '@/components/campaign-delivery-action';
import { CampaignStatusLine } from '@/components/campaign-status-line';
import { Icons } from '@/components/primitives';
import { campaignStatusLabel } from '@/lib/campaign-status';
import { RequiredField } from '@/components/required-field';
import { fmtDay, fmtDayShort } from '@/lib/datetime';
import { PageActions } from '@/components/page-actions';
import { providerLabel } from './create-publishing-modal';
import type { CreateFlowModel } from './create-flow-model';

export function CreateFlowHeader({ model }: { model: CreateFlowModel }) {
  const { campaign, plan, setView, status, production, channel, startDate, endDate, name, setName,
    savedName, setRenaming, actionError, actionNotice, required, cancelLaunchWhenReady,
    pauseCampaign, resumeCampaign, open, toggle, savedCampaignId, moreOpen, setMoreOpen, moreRef,
    publishTarget, failedSyncLeg, operationalStatus, publishSyncFailure, setPublishLogRequest,
    inFlightOps, statusLine, statusRowNeeded, currentRequestedStatus, headerStatusKind,
    armedStatusLine, togglePauseResume, handleDuplicate, handleRetrySync, handleArchive,
    handleDelete, commitName, handleDisarm, extendAvailable, handleOpenExtend, extensionSignal,
  } = model;
  const published = currentRequestedStatus === 'live' || currentRequestedStatus === 'paused' || currentRequestedStatus === 'ended';
  return (
    <>
      <div className="page-hd campaign-page-hd">
        <div className="create-page-title">
          <div className="campaign-name-block">
            {/* The visible title is the name's own editor, so the page heading
                is spoken only: without it the page had no heading at all. */}
            <h1 className="sr-only">{campaign ? (name.trim() || 'Untitled campaign') : 'New campaign'}</h1>
            <div className="campaign-name-row">
            <RequiredField
              fieldKey="campaign-name"
              message={required.messages['campaign-name']}
              attempt={required.attempt}
            >
              <label htmlFor="campaign-name-title" className="sr-only">Campaign name</label>
              {/* A phone header shares its row with the status pill and the
                  primary action, and an input cannot wrap — so on phones the
                  name is wrapping text that opens a rename sheet. */}
              <button
                type="button"
                className="campaign-name-static phone-only"
                aria-label="Rename campaign"
                onClick={() => setRenaming(true)}
              >
                {name || (campaign ? 'Name this campaign' : 'Name the new campaign')}
              </button>
              {/* The input sizes to its text through a hidden mirror in the same
                  grid cell, so the status chip sits right after the name. */}
              <span className="campaign-name-sizer phone-hide">
                <span className="campaign-name-mirror" aria-hidden="true">{name || (campaign ? 'Name this campaign' : 'Name the new campaign')}</span>
              <input
                id="campaign-name-title"
                className="campaign-name-title"
                value={name}
                placeholder={campaign ? 'Name this campaign' : 'Name the new campaign'}
                onChange={(event) => {
                  setName(event.target.value);
                  if (event.target.value.trim()) required.clear('campaign-name');
                }}
                onBlur={() => { void commitName(); }}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') { event.preventDefault(); event.currentTarget.blur(); }
                  if (event.key === 'Escape') { setName(savedName); event.currentTarget.blur(); }
                }}
              />
              </span>
            </RequiredField>
            {/* The permanent chip beside the name — LIVE, PAUSED, NEEDS ATTENTION…
                — and, while creative changes run, their count (Gio, 2026-09-18,
                canvas board 14). */}
            {campaign && (
              <span className="campaign-status-chip" data-tone={statusLine?.chip.tone ?? 'quiet'}>
                <span className="d" aria-hidden="true" />
                {statusLine ? statusLine.chip.label : campaignStatusLabel(headerStatusKind)}
              </span>
            )}
            {inFlightOps > 0 && (
              <button
                type="button"
                className="campaign-progress-pill"
                onClick={() => {
                  if (!open.cr) toggle('cr');
                  const first = [...(plan?.creative_slot_changes ?? []), ...(plan?.creative_rollouts ?? [])]
                    .find((row) => row.status === 'queued' || row.status === 'running');
                  requestAnimationFrame(() => document.getElementById(`campaign-creative-slot-${first?.position ?? 0}-card`)?.scrollIntoView({ block: 'center', behavior: 'smooth' }));
                }}
              >
                <span className="creative-slot-spinner" aria-hidden="true" /> {inFlightOps} change{inFlightOps === 1 ? '' : 's'} in progress
              </button>
            )}
            </div>
            <div className="campaign-name-meta">
              {/* A published campaign's plan is frozen, so its page is for viewing
                  (2026-10-19); creatives and delivery still change in place. */}
              <span>{!campaign ? 'New campaign' : published ? 'View campaign' : 'Edit campaign'}</span>
              {channel && <><span className="sep">·</span><span>{{meta:'Meta',ctv:'CTV',dsp:'Display'}[channel]}</span></>}
              {production && <><span className="sep phone-hide">·</span><span className="plain phone-hide">{production.title}</span></>}
              {plan?.revision && <><span className="sep">·</span><span>Revision {plan.revision}</span></>}
              {startDate && endDate && <><span className="sep phone-hide">·</span><span className="phone-hide">{fmtDayShort(startDate)} → {fmtDay(endDate)}</span></>}
              {statusLine?.checked && <><span className="sep">·</span><span>{statusLine.checked}</span></>}
            </div>
          </div>
        </div>
        <PageActions style={{gap: 8, alignItems: 'center'}}>
          {/* On a phone this lives in the ⋯ sheet: board 9's header carries no pause. */}
          {campaign && <CampaignDeliveryAction data-phone="overflow"
            status={operationalStatus} requestedState={status}
            pending={pauseCampaign.isPending || resumeCampaign.isPending}
            onRequest={(next) => { void togglePauseResume(next); }}
          />}
          {campaign && (
            <div style={{position:'relative'}} ref={moreRef} data-phone="hidden">
              <button
                className="btn btn-icon"
                onClick={() => setMoreOpen(o => !o)}
                aria-label="More actions"
                aria-expanded={moreOpen}
                style={{padding:'6px 8px'}}
              >{Icons.more}</button>
              {moreOpen && (
                <div style={{
                  // Opens toward the side with room: when the actions wrap under the title
                  // (a tablet width) the ⋯ sits at the left edge, and a right-anchored menu
                  // ran off the screen behind the sidebar.
                  position:'absolute', top:'calc(100% + 4px)', minWidth: 200,
                  ...((moreRef.current?.getBoundingClientRect().right ?? Infinity) < 216 ? { left: 0 } : { right: 0 }),
                  background:'var(--panel)', border:'1px solid var(--line-strong)',
                  borderRadius:'var(--r)', boxShadow:'0 12px 32px -8px rgba(0,0,0,.25)',
                  padding: 4, zIndex: 50,
                }}>
                  {failedSyncLeg && (
                    <div className="dd-item" onClick={() => { void handleRetrySync(); }}>
                      <span style={{display:'inline-flex',color:'var(--ink-2)'}}>{Icons.refresh}</span>
                      <span>Retry {failedSyncLeg === 'ttd' ? 'TTD' : 'Meta'} sync</span>
                    </div>
                  )}
                  {/* A live or paused campaign takes one more flight when its provider's
                      switch is on (campaign lifecycle 2b, 2026-10-21). A button, so the
                      keyboard reaches it. */}
                  {extendAvailable && (
                    <button type="button" className="dd-item" onClick={handleOpenExtend}>
                      <span style={{display:'inline-flex',color:'var(--ink-2)'}}>{Icons.calendar}</span>
                      <span>Extend flight…</span>
                    </button>
                  )}
                  <div className="dd-item" onClick={() => { void handleDuplicate(); }}>
                    <span style={{display:'inline-flex',color:'var(--ink-2)'}}>{Icons.copy}</span>
                    <span>Duplicate</span>
                  </div>
                  {(currentRequestedStatus === 'live' || currentRequestedStatus === 'paused') && (
                    <div className="dd-item" onClick={() => { void handleArchive(); }}>
                      <span style={{display:'inline-flex',color:'var(--ink-2)',width:14,height:14}}>
                        <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                          <rect x="2" y="3" width="12" height="3"/><rect x="3" y="6" width="10" height="7"/><path d="M6.5 9h3"/>
                        </svg>
                      </span>
                      <span>Archive</span>
                    </div>
                  )}
                  {currentRequestedStatus === 'draft' && (
                    <>
                      <div className="dd-divider"/>
                      <div className="dd-item" style={{color:'var(--bad)'}} onClick={() => { void handleDelete(); }}>
                        <span style={{display:'inline-flex'}}>
                          <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M3 5h10M6.5 5V3.5h3V5M5 5l.5 8.5h5L11 5"/>
                          </svg>
                        </span>
                        <span>Delete…</span>
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          )}
          {/* The desktop ⋯ menu above is dropped on phones; these carry the same
              actions into the PageActions sheet and are display:none on desktop. */}
          {campaign && failedSyncLeg && (
            <button className="btn phone-only" onClick={() => { void handleRetrySync(); }}>
              <span style={{display:'inline-flex',color:'var(--ink-2)'}}>{Icons.refresh}</span>Retry {failedSyncLeg === 'ttd' ? 'TTD' : 'Meta'} sync
            </button>
          )}
          {campaign && extendAvailable && (
            <button type="button" className="btn phone-only" onClick={handleOpenExtend}>
              <span style={{display:'inline-flex',color:'var(--ink-2)'}}>{Icons.calendar}</span>Extend flight…
            </button>
          )}
          {campaign && (
            <button className="btn phone-only" onClick={() => { void handleDuplicate(); }}>
              <span style={{display:'inline-flex',color:'var(--ink-2)'}}>{Icons.copy}</span>Duplicate
            </button>
          )}
          {campaign && (currentRequestedStatus === 'live' || currentRequestedStatus === 'paused') && (
            <button className="btn phone-only" onClick={() => { void handleArchive(); }}>Archive</button>
          )}
          {campaign && currentRequestedStatus === 'draft' && (
            <button className="btn phone-only" style={{color:'var(--bad)'}} onClick={() => { void handleDelete(); }}>Delete…</button>
          )}
          <button className="btn btn-ghost campaign-back" data-phone="inline" aria-label={campaign ? 'Done' : 'Cancel'} onClick={() => setView('dashboard')}>
            <span className="phone-long">{campaign ? 'Done' : 'Cancel'}</span>
            <span className="phone-short" aria-hidden="true"><svg width="18" height="18" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M10 3L5 8l5 5" /></svg></span>
          </button>
        </PageActions>
          {/* The header's third line, full width under name and actions, only
              when the chip beside the name and the meta line cannot say it: a
              pause reason, a repair in progress, an attention sentence with the
              retry, the armed chip (Gio, 2026-09-18). */}
          {(statusRowNeeded || headerStatusKind === 'armed') && (
            <div className="campaign-status-row">
          {statusRowNeeded && savedCampaignId && (
            <CampaignStatusLine
              status={operationalStatus}
              provider={providerLabel(publishTarget)}
              campaignId={savedCampaignId}
              onOpenPublishLog={(event) => { event.preventDefault(); setPublishLogRequest((n) => n + 1); }}
              failure={publishSyncFailure}
              extension={extensionSignal}
              onOpenExtension={handleOpenExtend}
            />
          )}
          {headerStatusKind === 'armed' && (
            <div style={{display:'inline-flex',alignItems:'center',gap:8,padding:'4px 10px',background:'var(--bg-sunk)',border:'1px solid var(--line)',borderRadius:999,flexShrink:0,fontSize:10.5}}>
              {/* The status chip already says ARMED. This one exists for the two things
                  it alone can say: what it is still waiting on, and how to stop it. */}
              <span className="mono" style={{letterSpacing:'.08em', color:'var(--ink-2)'}}>
                {armedStatusLine}
              </span>
              <button
                className="btn btn-sm btn-ghost"
                style={{padding:'0 4px',fontSize:11}}
                title="Disarm — the campaign stays a draft until launched manually"
                disabled={cancelLaunchWhenReady.isPending}
                onClick={() => { void handleDisarm(); }}
              >
                ×
              </button>
            </div>
          )}
            </div>
          )}
      </div>
      {(actionError || actionNotice) && (
        <div
          className="facet"
          role={actionError ? 'alert' : 'status'}
          style={{
            marginBottom: 12,
            borderColor: actionError ? 'var(--bad)' : 'var(--good)',
            color: actionError ? 'var(--bad)' : 'var(--ink)',
          }}
        >
          {actionError || actionNotice}
        </div>
      )}
    </>
  );
}
